<template>
  <div :class="['auth-location', { 'is-mobile': isMobile }]">
    <div class="auth-content">
      <div class="auth-context left">
        <span class="auth-title">{{ localize('auth.page.location.title') }}</span>
        <span class="auth-subtitle">{{ localize('auth.page.location.subtitle') }}</span>
      </div>

      <div v-if="arePermissionsResolved" class="auth-content-wrapper">
        <div class="auth-form loction">
          <spark-match-city :on-change="handleCityChange" />
          <span v-if="sparkInitError" class="auth-error">{{ sparkInitError }}</span>
        </div>
      </div>
    </div>

    <div class="auth-methods-group">
      <div class="methods-row" v-if="!arePermissionsResolved">
        <gam-button v-bind="getPermissionsButton" />
      </div>

      <div class="methods-row" v-if="!arePermissionsResolved">
        <gam-button v-bind="getSkipButton" />
      </div>

      <div class="methods-row" v-if="arePermissionsResolved">
        <gam-button v-bind="getContinueButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterAuthType, RouterNameType } from '@/core/data/config/uiConfig.interface';
import { type CityDto } from '@/core/data/location/location.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useCityStore } from '@/stores/city.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import GamButton from '@/views/components/GamButton.vue';
import { GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import SparkMatchCity from '@/views/main/sparks/components/SparkMatchCity.vue';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router';

const isAuthLoading = ref<boolean>(true);
const isAwaitingLocationPermission = ref<boolean>(false);
const arePermissionsSkipped = ref<boolean>(false);
const isAdvancingToProfileImages = ref<boolean>(false);
const sparkInitError = ref<string | null>(null);
// const route = useRoute();
const router = useRouter();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const cityStore = useCityStore();
const { city } = storeToRefs(cityStore);

const geolocationStore = useGeolocationStore();
const { userLocation } = storeToRefs(geolocationStore);

const cityChangeId = ref<number>(0);
const isCityInitialized = ref<boolean>(false);
const cityBootstrapPromise = ref<Promise<void> | null>(null);

// Skip the permission step during onboarding — show city search directly.
// The permission buttons remain in the template so this can be re-enabled later.
const arePermissionsResolved = computed(() => true);

// Original logic kept for reference:
// const arePermissionsResolved = computed(
//   () =>
//     isLocationPermissionGranted.value ||
//     userLocation.value?.source === GeoDataSource.BROWSER_API ||
//     arePermissionsSkipped.value,
// );

const runCityBootstrap = async (item: GamDropdownItem | CityDto, options?: { confirm?: boolean }): Promise<void> => {
  try {
    sparkInitError.value = null;
    isCityInitialized.value = false;

    await cityStore.determineCity(item.coordinates);
    await nextTick();

    if (!city.value?.id) {
      sparkInitError.value = localize('auth.page.location.error.cityRequired');
      return;
    }

    if (options?.confirm) {
      cityStore.confirmCity();
    }

    isCityInitialized.value = true;
    await nextTick();
  } catch {
    sparkInitError.value = localize('auth.page.location.error.cityRequired');
  }
};

const handleCityChange = async (item: GamDropdownItem | CityDto | null): Promise<void> => {
  if (!item?.coordinates) {
    return;
  }

  const thisChangeId = ++cityChangeId.value;

  // Yield one microtask so synchronous duplicate calls all increment the
  // counter before any of them proceed — only the latest one continues.
  await Promise.resolve();

  if (cityChangeId.value !== thisChangeId) {
    return;
  }

  if (cityBootstrapPromise.value) {
    await cityBootstrapPromise.value;
  }

  if (cityChangeId.value !== thisChangeId) {
    return;
  }

  const bootstrapPromise = runCityBootstrap(item, { confirm: true });
  cityBootstrapPromise.value = bootstrapPromise;

  try {
    await bootstrapPromise;
  } finally {
    if (cityBootstrapPromise.value === bootstrapPromise) {
      cityBootstrapPromise.value = null;
    }
  }
};

const advanceToProfileImages = async () => {
  if (isAdvancingToProfileImages.value) return;

  isAdvancingToProfileImages.value = true;
  isAwaitingLocationPermission.value = false;
  sparkInitError.value = null;

  try {
    if (cityBootstrapPromise.value) {
      await cityBootstrapPromise.value;
    }

    await authStore.fetchUserInfo(true);
    await nextTick();

    const cityId = userDetails.value?.city?.id || city.value?.id;

    if (!cityId) {
      sparkInitError.value = localize('auth.page.location.error.cityRequired');
      return;
    }

    const nextRouteName = userDetails.value?.profileImageBlobHash
      ? RouterNameType.SPARKS
      : RouterAuthType.AUTH_PROFILE_IMAGES;

    await router.push({ name: nextRouteName });
  } catch {
    sparkInitError.value = localize('auth.page.location.error.cityRequired');
  } finally {
    isAdvancingToProfileImages.value = false;
  }
};

const getPermissionsButton = computed(
  (): GamButtonType => ({
    label: localize('auth.page.registration.form.location.button'),
    isFullSize: true,
    isCentered: true,
    isLoading: isAwaitingLocationPermission.value,
    onClick: async () => {
      isAwaitingLocationPermission.value = true;

      if (arePermissionsResolved.value) {
        return;
      }

      geolocationStore.setLocationPermissionRequested(true);

      gambits.geolocationService.getCurrentPosition();
      gambits.geolocationService.startTracking();
    },
  }),
);

const getSkipButton = computed(
  (): GamButtonType => ({
    label: localize('auth.page.registration.form.location.skip'),
    variant: GamButtonVariant.SLAB,
    isFullSize: true,
    isCentered: true,
    onClick: () => {
      arePermissionsSkipped.value = true;
    },
  }),
);

const getContinueButton = computed(
  (): GamButtonType => ({
    label: localize('auth.page.registration.form.location.continue'),
    isLoading: isAdvancingToProfileImages.value,
    isDisabled: Boolean(cityBootstrapPromise.value),
    onClick: () => {
      advanceToProfileImages();
    },
  }),
);

onBeforeMount(async () => {
  await getAuthUser();

  const { firstName, lastName, birthDate, username, rating } = userDetails.value || {};

  if (!authStore.isAuthenticated()) {
    return router.push('/auth');
  }
  if (!firstName || !lastName || !birthDate || !username) {
    return router.push('/auth/registration');
  }
  if (!rating) {
    return router.push('/auth/ratings');
  }

  if (city.value?.id) {
    cityStore.confirmCity();
  } else if (userLocation.value?.location) {
    cityStore.determineCity();
  }
});

const getAuthUser = async (): Promise<void> => {
  await authStore.fetchUserInfo(true);

  isAuthLoading.value = false;
};
</script>

<style lang="scss" scoped>
#auth-view .auth-modal .auth-wrapper .auth-content {
  padding-bottom: 0 !important;
  gap: 1rem;
}

.auth-content-wrapper {
  width: 100%;
}

.auth-form.loction {
  padding: 0 2rem;
}

.auth-error {
  display: block;
  margin-top: 0.75rem;
  color: var(--color-pink);
}
</style>
